import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { ChevronDown, LogOut, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { Spinner } from '@/components/Spinner'
import { useAuth, accountName } from '@/lib/auth'
import { useLocale } from '@/lib/useLocale'
import { cn } from '@/lib/utils'

/** Dashboard home for a given role. Owners/admins land on Ingresos — the
 *  revenue overview is their default "how am I doing" landing. A company or
 *  autónomo lands on Reservas (/dashboard/bookings) with a visual summary of
 *  their bookings, then jumps to Search to book more. Agencies and employees
 *  land on the browse view at /dashboard. Shared by the brand link, the public
 *  header and the landing auth-gate so they always agree. */
export function dashboardHomePath(role: string | undefined, locale: string): string {
  if (role === 'owner' || role === 'admin' || role === 'superadmin')
    return `/${locale}/dashboard/revenue`
  if (role === 'company' || role === 'autonomo')
    return `/${locale}/dashboard/bookings`
  return `/${locale}/dashboard`
}

/** Brand block — wordmark "bitutravel" first, then the logo icon on its right,
 *  consistent across every header. The wordmark is always visible (one piece). */
function BrandLink({ to, className }: { to: string; className?: string }) {
  return (
    <Link to={to} className={cn('flex min-w-0 items-center gap-2 font-heading text-xl font-bold tracking-tight sm:text-2xl', className)}>
      <span className="whitespace-nowrap">
        <span className="text-primary">bitu</span>
        <span className="text-travel">travel</span>
      </span>
      <img
        src="/bitutravel-logo.png"
        alt="bitutravel"
        className="h-8 w-8 shrink-0 rounded-full sm:h-9 sm:w-9"
      />
    </Link>
  )
}

export function PublicLayout() {
  const { t } = useTranslation()
  const { session, profile } = useAuth()
  const locale = useLocale()

  return (
    <div className="flex min-h-full flex-col bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-2 gap-y-2 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {/* Logged in → the wordmark is the way back to the dashboard (no
                separate "Panel/Resumen" link). Logged out → the public landing. */}
            <BrandLink to={session && profile ? dashboardHomePath(profile.role, locale) : `/${locale}`} />
          </div>
          <nav className="flex shrink-0 items-center gap-1 sm:gap-2">
            <LanguageSwitcher />
            {session && profile ? (
              <LogoutButton />
            ) : (
              <>
                <Button asChild variant="ghost" size="sm">
                  <NavLink to="auth/login">{t('nav.login')}</NavLink>
                </Button>
                <Button asChild size="sm">
                  <NavLink to="auth/register">{t('nav.register')}</NavLink>
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-border bg-muted/40 py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 text-xs text-muted-foreground sm:flex-row sm:justify-between">
          <span>bitutravel · España · {new Date().getFullYear()}</span>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to={`/${locale}/legal/aviso-legal`} className="hover:text-foreground">
              {t('nav.legalNotice')}
            </Link>
            <Link to={`/${locale}/legal/privacidad`} className="hover:text-foreground">
              {t('nav.privacy')}
            </Link>
            <Link to={`/${locale}/legal/cookies`} className="hover:text-foreground">
              {t('nav.cookies')}
            </Link>
            <Link to={`/${locale}/legal/terminos`} className="hover:text-foreground">
              {t('nav.terms')}
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  )
}

export function AppLayout() {
  const { t } = useTranslation()
  const { profile } = useAuth()
  const locale = useLocale()
  const isCompany = profile?.role === 'company' || profile?.role === 'autonomo'
  const isOwner = profile?.role === 'owner'
  const isAgency = profile?.role === 'agency'
  // Employees book on the company's behalf — they see Reservas + Perfil only.
  const isEmployee = profile?.role === 'employee'

  // Pill-style nav: muted tray, the active route gets the blue travel accent
  // so the current section reads at a glance — consistent with the app's accent.
  const pillClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors sm:text-sm',
      isActive
        ? 'bg-travel font-medium text-white shadow-sm'
        : 'text-muted-foreground hover:text-foreground',
    )

  // The logo/wordmark lands on the role's home section.
  const dashboardHome = dashboardHomePath(profile?.role, locale)

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3">
          <BrandLink to={dashboardHome} />
          <nav className="order-3 flex flex-wrap items-center gap-0.5 rounded-full bg-muted p-1 sm:order-none">
            {/* The role's landing view (the search/browse view for bookers) gets
                its own pill so the active state shows where you landed. Owners
                already have "Ingresos" as their home pill. */}
            {(isCompany || isEmployee || isAgency) && (
              <NavLink to={`/${locale}/dashboard`} end className={pillClass}>
                {t('nav.search')}
              </NavLink>
            )}
            {(isCompany || isEmployee || isAgency) && (
              <NavLink to={`/${locale}/dashboard/bookings`} className={pillClass}>
                {t('nav.reservas')}
              </NavLink>
            )}
            {/* Facturas — the company's invoices, right next to Reservas. Owners
                get their own (issued) invoices pill further along. */}
            {isCompany && (
              <NavLink to={`/${locale}/dashboard/invoices`} className={pillClass}>
                {t('invoices.navInvoices')}
              </NavLink>
            )}
            {isAgency && (
              <NavLink to={`/${locale}/dashboard/clients`} className={pillClass}>
                {t('nav.clientes')}
              </NavLink>
            )}
            {isOwner && (
              <NavLink to={`/${locale}/dashboard/revenue`} className={pillClass}>
                {t('owner.revenueTitle')}
              </NavLink>
            )}
            {isOwner && (
              <NavLink to={`/${locale}/dashboard/properties`} className={pillClass}>
                {t('owner.propertiesNav')}
              </NavLink>
            )}
            {isOwner && (
              <NavLink to={`/${locale}/dashboard/products`} className={pillClass}>
                {t('owner.products.nav')}
              </NavLink>
            )}
            {isOwner && (
              <NavLink to={`/${locale}/dashboard/invoices`} className={pillClass}>
                {t('invoices.navInvoices')}
              </NavLink>
            )}
            {isCompany && (
              <NavLink to={`/${locale}/dashboard/team`} className={pillClass}>
                {t('nav.equipo')}
              </NavLink>
            )}
          </nav>
          {/* Account menu — the profile is edited rarely, so it lives behind the
              name (standard SaaS pattern), not as a nav pill. */}
          <AccountMenu showProfile />
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Outlet />
      </main>
    </div>
  )
}

export function AdminLayout() {
  const { t } = useTranslation()
  const locale = useLocale()
  const adminNav: { to: string; labelKey: string }[] = [
    { to: `/${locale}/admin/approvals`, labelKey: 'admin.navApprovals' },
    { to: `/${locale}/admin/accounts`, labelKey: 'admin.navAccounts' },
    { to: `/${locale}/admin/bookings`, labelKey: 'admin.navBookings' },
    { to: `/${locale}/admin/invoices`, labelKey: 'admin.navInvoices' },
    { to: `/${locale}/admin/registry`, labelKey: 'admin.navRegistry' },
  ]
  return (
    <div className="mx-auto flex min-h-full max-w-7xl flex-col px-4 py-6">
      <header className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Brand — wordmark then logo, links to the public landing. */}
          <BrandLink to={`/${locale}`} />
          <span className="text-muted-foreground/60">·</span>
          <h1 className="font-heading text-xl font-semibold">{t('admin.consoleTitle')}</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* Account menu (admin console has no profile route — sign out only). */}
          <AccountMenu />
        </div>
      </header>
      {/* Admin nav pills — Approvals, Accounts, Bookings, Invoices. */}
      <nav className="mb-6 flex flex-wrap gap-2">
        {adminNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end
            className={({ isActive }) =>
              'rounded-full border px-3 py-1 text-sm font-medium transition-colors ' +
              (isActive
                ? 'border-travel bg-travel/10 text-travel'
                : 'border-border text-foreground hover:bg-muted/60')
            }
          >
            {t(n.labelKey)}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}

/** Account menu — the signed-in user's name (or initials on mobile) opens a
 *  dropdown with their email, a Profile link (when showProfile) and Sign out.
 *  Standard SaaS pattern: profile is rarely edited, so it lives behind the
 *  name, not as a nav pill. */
function AccountMenu({ showProfile = false }: { showProfile?: boolean }) {
  const { t } = useTranslation()
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const locale = useLocale()
  const [open, setOpen] = useState(false)
  const name = accountName(profile)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 text-left transition-colors hover:bg-muted/60"
          aria-label={name || profile?.email || t('nav.logout')}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-travel/10 text-xs font-semibold text-travel">
            {initials(name) || <User className="h-4 w-4" />}
          </span>
          <span className="hidden sm:block sm:max-w-40">
            <span className="block truncate text-sm font-medium leading-tight">{name}</span>
            <span className="block truncate text-xs leading-tight text-muted-foreground">{profile?.email}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        {/* Mobile context: the name/email are hidden in the trigger on small
            screens, so show them in the menu. */}
        <div className="mb-1 border-b border-border px-2 py-2 sm:hidden">
          <p className="truncate text-sm font-medium">{name}</p>
          <p className="truncate text-xs text-muted-foreground">{profile?.email}</p>
        </div>
        {showProfile && (
          <NavLink
            to={`/${locale}/dashboard/profile`}
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted"
          >
            <User className="h-4 w-4" /> {t('profile.navProfile')}
          </NavLink>
        )}
        <button
          type="button"
          onClick={async () => {
            setOpen(false)
            await signOut()
            navigate(`/${locale}`)
          }}
          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
        >
          <LogOut className="h-4 w-4" /> {t('nav.logout')}
        </button>
      </PopoverContent>
    </Popover>
  )
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function LogoutButton() {
  const { t } = useTranslation()
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const locale = useLocale()
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={t('nav.logout')}
      title={t('nav.logout')}
      onClick={async () => {
        await signOut()
        navigate(`/${locale}`)
      }}
    >
      <LogOut className="h-4 w-4" />
    </Button>
  )
}

export function FullScreenLoader() {
  return (
    <div className="flex min-h-full items-center justify-center">
      <Spinner className="h-6 w-6" />
    </div>
  )
}

export function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col justify-center px-4 py-12">
      <Card className="p-6">{children}</Card>
    </div>
  )
}